/**
 * R6 — the registry, and the ONE new authorization behaviour in the release (D-10).
 *
 * Today an app developer sees no console section at all, and R6 is the release that fixes it.
 * The chain, measured on HEAD:
 *   1. `app_owner` / `app_admin` / `app_maintainer` / `app_viewer` are all
 *      `isResourceScoped: true` (`cp-auth-api/src/modules/permissions/builtin-roles.ts`). Until
 *      2026-10-20-cp-permissioning the per-app developer role was `app_developer`; that slug is now
 *      a site role that holds `cp:app:create` (D1, D2) and five own-namespace private-channel
 *      strings (2026-10-26-appdev-permissions A-001), and no app read.
 *   2. `PermissionSnapshotBuilder.buildForOperator` DELIBERATELY never expands resource
 *      grants into `sitePermissions` — that expansion IS the IDOR, removed after the
 *      corridor-security review of PR #192. So the JWT of a per-app developer (a holder of
 *      per-app grants alone) carries `sitePermissions: []`. A holder of the site `app_developer`
 *      role carries `cp:app:create`, which opens the `app-catalog` entry.
 *   3. The old shell's `manifestAllows` unions only
 *      `siteRoles ∪ sitePermissions ∪ orgRoles ∪ orgPermissions` — `resourceGrants` is not
 *      read at all.
 *   4. ⇒ nothing is visible, and `ZeroAccessPanel` renders, whose own comment reads
 *      "Console-section visibility is site-level in R2; a grant-derived section is R6's."
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { hasPermission } from '@zion/sdk-contracts';
import { usePermissionSnapshot, type PermissionSnapshot } from '@zion/sdk-react';
import {
  APP_SCOPED_ENTRIES,
  GLOBAL_ENTRIES,
  type AppScopedEntry,
  type GlobalEntry,
  type GrantResourceType,
  type ManifestPermission,
} from './manifest';

/**
 * True iff `snap` satisfies `req`, counting BOTH site-level permissions and the principal's
 * resource grants of type `grantResourceType` (D-10).
 *
 * ⛔ TWO ARGUMENTS, NOT ONE CONCATENATED LIST. Merging grant permissions into the site list
 * would make "holds cp:app:read everywhere" and "holds cp:app:read on exactly one app"
 * indistinguishable at every later call site — which is the IDOR
 * `PermissionSnapshotBuilder` deliberately refuses to create server-side.
 *
 * ⛔ This function answers ONE question — "should this nav entry render?" — and its answer
 * must NEVER be reused as an authorization decision. It widens NAV VISIBILITY ONLY. Per-app
 * controls keep using `useHasPermission(permission, appId)` from `@zion/sdk-react`, whose
 * resource leg is `g.permission === permission && g.resourceId === resourceId` — exact string
 * equality, no wildcard expansion. Showing a section that contains only controls the
 * principal cannot use is a bad screen; expanding grants into the site set is an IDOR. R6
 * does the former, never the latter.
 */
export function entryAllows(
  snap: PermissionSnapshot,
  req: ManifestPermission,
  grantResourceType?: GrantResourceType,
): boolean {
  const site: readonly string[] = [
    ...snap.siteRoles,
    ...snap.sitePermissions,
    ...snap.orgRoles,
    ...snap.orgPermissions,
  ];
  const wanted = typeof req === 'string' ? [req] : req.anyOf;

  // Site leg — `hasPermission` from @zion/sdk-contracts, so `*` and `prefix:*` behave
  // exactly as every guard behaves. ⛔ Never re-implement it.
  if (wanted.some((p) => hasPermission(site, p))) return true;

  if (grantResourceType === undefined) return false;

  // Grant leg — EXACT equality on the permission string, matching `useHasPermission`'s
  // resource leg in @zion/sdk-react. ⛔ A wildcard here would make nav visibility MORE
  // permissive than the control checks inside the page, producing a section full of
  // disabled buttons.
  return snap.resourceGrants.some(
    (g) => g.resourceType === grantResourceType && wanted.includes(g.permission),
  );
}

export interface AppRegistryState {
  readonly globalEntries: readonly GlobalEntry[];
  readonly appScopedEntries: readonly AppScopedEntry[];
  readonly visibleGlobal: readonly GlobalEntry[];
  readonly visibleAppScoped: readonly AppScopedEntry[];
}

const AppRegistryCtx = createContext<AppRegistryState | null>(null);

export interface AppRegistryProviderProps {
  readonly children: ReactNode;
  /**
   * Overrides for tests, so a two-section registry can be constructed (WS-D §10 assertion 7)
   * without shipping a second section. Defaults to the real manifest.
   */
  readonly globalEntries?: readonly GlobalEntry[];
  readonly appScopedEntries?: readonly AppScopedEntry[];
}

export function AppRegistryProvider({
  children,
  globalEntries = GLOBAL_ENTRIES,
  appScopedEntries = APP_SCOPED_ENTRIES,
}: AppRegistryProviderProps): React.JSX.Element {
  const snap = usePermissionSnapshot();
  const value = useMemo<AppRegistryState>(
    () => ({
      globalEntries,
      appScopedEntries,
      visibleGlobal: globalEntries.filter((e) =>
        entryAllows(snap, e.requiredPermission, e.grantResourceType),
      ),
      visibleAppScoped: appScopedEntries.filter((e) =>
        entryAllows(snap, e.requiredPermission, e.grantResourceType),
      ),
    }),
    [globalEntries, appScopedEntries, snap],
  );
  return <AppRegistryCtx value={value}>{children}</AppRegistryCtx>;
}

export function useAppRegistry(): AppRegistryState {
  const ctx = useContext(AppRegistryCtx);
  // Throw rather than return an empty registry: an empty one is indistinguishable from "this
  // principal can see nothing", so a missing provider would surface as a plausible-looking
  // zero-access screen instead of a stack trace.
  if (ctx === null) throw new Error('useAppRegistry must be used within an AppRegistryProvider');
  return ctx;
}
